import {
  GROUP_DELIVERY_METHOD,
  GROUP_MARKET_TYPES,
} from '@sonifi/wb-group/constants/wbGroupConstants';
import GroupChannel from '../properties/components/ProductsSettings/GroupChannel';
import GuestRequest from '../properties/components/ProductsSettings/GuestRequest';
import HappeningsChannel from '../properties/components/ProductsSettings/HappeningsChannel';
import HotelDirectory from '../properties/components/ProductsSettings/HotelDirectory';
import InRoomDinning from '../properties/components/ProductsSettings/InRoomDinning';
import NoDetails from '../properties/components/ProductsSettings/NoDetails';
import RetailShopping from '../properties/components/ProductsSettings/RetailShopping';
import Staycast from '../properties/components/ProductsSettings/Staycast';
import Reporting from '../properties/components/ProductsSettings/Reporting';

export const JOB_TYPES = Object.freeze({
  PUBLISH_SINGLE_CHANNEL: 'PUBLISH_SINGLE_CHANNEL',
  PUBLISH_IRD_MENU: 'PUBLISH_IRD_MENU',
  PUBLISH_SERVICE_MENU: 'PUBLISH_SERVICE_MENU',
  TRANSCODE: 'TRANSCODE',
});

// Toggle rendered above the Admin > Users table. Its state is the
// `showDeactivated` URL search param; the filter itself runs on the server
// (`GET /users?enabled=true`), so no per-row predicate lives here.
export const HIDE_DEACTIVATED_USERS_FILTER = Object.freeze({
  id: 'adminUsersHideDeactivated',
  name: 'Hide deactivated users',
  description: 'Hides deactivated users',
});

export const PRODUCTS_PER_GROUP_TYPE = Object.freeze({
  brand: ['reporting'],
  property: [
    'branding',
    'group_channel',
    'guest_request',
    'happenings_channel',
    'hotel_directory',
    'ird',
    'reporting',
    'staycast',
  ],
});

export const PRODUCTS_DETAILS = Object.freeze({
  group_channel: () => GroupChannel,
  happenings_channel: () => HappeningsChannel,
  hotel_directory: () => HotelDirectory,
  ird: (groupType) =>
    groupType === GROUP_MARKET_TYPES.healthcare
      ? RetailShopping
      : InRoomDinning,
  noDetails: () => NoDetails,
  staycast: () => Staycast,
  reporting: () => Reporting,
  guest_request: () => GuestRequest,
});

export const DEFAULT_CHANNEL_SETTING = Object.freeze({
  duration_limit: 1200,
  label: '',
});

export const ORDERING_MODES = Object.freeze({
  email: false,
  on_site: false,
  read_only: false,
});

export const STAYCAST_PUBLIC_KEY_PATTERN =
  /^-{5}BEGIN PUBLIC KEY-{5}\n([a-zA-Z0-9+/]{64}\n){6,15}[a-zA-Z0-9+/=]{1,64}\n-{5}END PUBLIC KEY-{5}\n?$/;

export const PRODUCTS_SETTINGS_TO_SUBMIT = {
  common: ['enabled', 'group', 'id', 'product', 'uri'],
  branding: ['background'],
  happenings_channel: [
    'active_time_support',
    'channel_settings',
    'number_of_channels',
  ],
  group_channel: ['number_of_channels'],
  hotel_directory: ['tv_menu_style', 'enable_qr_codes', 'enable_fullscreen_image_regions'],
  ird: ['available_ordering_modes', 'number_of_revenue_centers'],
  reporting: ['dashboard_mode'],
  staycast: [],
  guest_request: [
    'delivery_method',
    'display_category_images',
    'display_name',
    'service_api_endpoint',
    'service_api_password',
    'service_api_username',
    'synchronizes',
    'synchronization_delay',
    'order_by_option',
  ],
  medications: [],
};

export const STAYCAST_BRANDING_KEYS = Object.freeze({
  tokens: 'tokens',
  defaults: 'defaults',
  hyatt: 'hyatt',
  marriott: 'marriott',
});

export const DEFAULT_PRODUCTS_SETTINGS = Object.freeze({
  branding: {
    background: null,
    enabled: true,
    group: null,
    id: null,
    uri: null,
  },
  group_channel: {
    enabled: false,
    group: null,
    id: null,
    number_of_channels: 1,
    uri: null,
  },
  guest_request: {
    delivery_method: GROUP_DELIVERY_METHOD.integrated,
    display_category_images: true,
    display_name: 'Guest Request',
    enabled: false,
    group: null,
    id: null,
    last_updated: null,
    order_by_option: null,
    service_api_endpoint: null,
    service_api_password: null,
    service_api_username: null,
    synchronization_delay: null,
    synchronizes: null,
    uri: null,
  },
  happenings_channel: {
    active_time_support: 'day_of_month',
    channel_settings: [{ duration_limit: 1200 }],
    enabled: true,
    group: null,
    id: null,
    number_of_channels: 1,
    uri: null,
  },
  hotel_directory: {
    last_updated: null,
    enabled: false,
    group: null,
    id: null,
    uri: null,
    tv_menu_style: 'vertical',
    enable_qr_codes: false,
    enable_fullscreen_image_regions: false,
  },
  ird: {
    available_ordering_modes: {
      email: true,
      on_site: false,
      read_only: false,
    },
    enabled: false,
    group: null,
    id: null,
    last_updated: null,
    number_of_revenue_centers: 1,
    uri: null,
  },
  reporting: {
    enabled: false,
    dashboard_mode: 'auto',
    group: null,
    id: null,
    uri: null,
  },
  staycast: {
    enabled: false,
    group: null,
  },
});
